import { queryKeys } from "@/client";
import generateSections from "@/components/DirectorySubmissions/Form/Sections";
import { useFieldLocking } from "@/components/DirectorySubmissions/Form/hooks/useFieldLocking";
import { useFieldSaving } from "@/components/DirectorySubmissions/Form/hooks/useFieldSaving";
import { useFormLocking } from "@/components/DirectorySubmissions/Form/hooks/useFormLocking";
import { useRealtimeFormUpdates } from "@/components/DirectorySubmissions/Form/hooks/useRealtimeFormUpdates";
import {
  type SIDE_PANELS_AVAILABLE,
  useSidePanelState,
} from "@/components/DirectorySubmissions/Form/hooks/useSidePanelState";
import {
  type DirectorySubmissionFull,
  type QuestionComments,
  type QuestionValue,
  type RevisionHistoryItems,
  type Section,
  type SubmissionFormData,
  type TableRow,
} from "@/components/DirectorySubmissions/Form/types";
import {
  loadSubmissionData,
  saveData,
  submissionToFormData,
} from "@/components/DirectorySubmissions/Form/utils";
import { useApprovalStatus } from "@/components/DirectorySubmissions/Form/utils/approvalUtils";
import { setupSubmissionSubscriptions } from "@/components/DirectorySubmissions/Form/utils/subscriptionUtils";
import RequestUnlockModal from "@/components/SimpleEditor/RequestUnlockModal";
import { type EditorValueHandle } from "@/components/SimpleEditor/SimpleEditor";
import { UnlockRequestReceivedModal } from "@/components/SimpleEditor/UnlockRequestReceivedModal";
import Spinner from "@/components/Spinner";
import { useAuth } from "@/contexts/AuthHook";
import { type UserProfile } from "@/util/hooks";
import { type SupabaseClient } from "@supabase/supabase-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import isEqual from "lodash/isEqual";
import { useSnackbar } from "notistack";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useBlocker, useParams } from "react-router-dom";

// Re-export the type for use in other components
export type { SIDE_PANELS_AVAILABLE };

interface SubmissionsContextValue {
  isSaving: boolean;
  setIsSaving: (value: boolean) => void;
  isFormLocked: boolean;
  submissionData: DirectorySubmissionFull | null;
  formData: SubmissionFormData;
  setFormData: React.Dispatch<React.SetStateAction<SubmissionFormData>>;
  initialFormData: SubmissionFormData | null;
  error: boolean;
  showLockWarning: () => void;
  updateLockState: (is_locked: boolean) => void;
  sidePanelOpen: SIDE_PANELS_AVAILABLE | null;
  openSidePanel: (panelName: SIDE_PANELS_AVAILABLE, questionId: string) => void;
  closeSidePanel: () => void;
  submissionComments: QuestionComments;
  submissionRevisions: RevisionHistoryItems;
  activeCommentQuestion: string | null;
  setActiveCommentQuestion: (sectionId: string | null) => void;
  saveField: (
    fieldId: string,
    value: any,
    onComplete?: () => void,
  ) => Promise<void>;
  pendingSaves: Set<string>;
  approvedQuestions: Set<string>;
  isQuestionApproved: (questionId: string) => boolean;
  pendingReviewQuestions: Set<string>;
  isQuestionPendingReview: (questionId: string) => boolean;
  updateField: (fieldId: string, value: any) => void;
  updateTableCell: (
    questionId: string,
    rowIndex: number,
    colIndex: number,
    value: any,
  ) => void;
  hasUnsavedChanges: boolean;
  updateApprovalStateAfterReorder: () => void;
  registerEditorRef: (id: string, ref: EditorValueHandle | null) => void;
  lockField: (fieldId: string, questionId?: string) => void;
  unlockField: (fieldId: string) => void;
  requestFieldUnlock: (data: {
    fieldId: string;
    lockingUserId: string;
    lockingUserDisplayName: string;
  }) => void;
  lockedFields: Record<string, string>;
  lockedQuestions: Record<string, string>;
  userProfiles: Record<string, UserProfile>;
  sections: Section[];
  currentlyEditingField: string | null;
}

export const SubmissionsContext = createContext<SubmissionsContextValue | null>(
  null,
);

export const SubmissionsProvider = ({
  children,
  submissionId: submissionIdProp,
}: {
  children: React.ReactNode;
  submissionId?: string;
}) => {
  const { submissionId: submissionIdFromParams } = useParams();
  const submissionId = submissionIdProp || submissionIdFromParams;
  const { supabase, firmId, userId } = useAuth();
  const [error, setError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<SubmissionFormData>({});
  const [showSavingModal, setShowSavingModal] = useState(false);

  const initialFormDataRef = useRef<SubmissionFormData | null>(null);
  const savingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const editorRefs = useRef<Map<string, EditorValueHandle>>(new Map());

  // Load submission data
  const {
    data: submissionData,
    isLoading,
    isRefetching,
    isSuccess: isLoadedSuccessfully,
  } = useQuery({
    queryKey: queryKeys.directorySubmission(submissionId ?? ""),
    queryFn: async (): Promise<DirectorySubmissionFull> => {
      if (!submissionId) {
        throw new Error("No submissionId");
      }
      try {
        return await loadSubmissionData(
          submissionId,
          supabase as SupabaseClient,
        );
      } catch (err) {
        setError(true);
        throw err;
      }
    },
    enabled: !!submissionId && !!supabase,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });

  // Update loading state based on refetching
  useEffect(() => {
    setIsSaving(isRefetching);
  }, [isRefetching]);

  const sections = useMemo(() => {
    if (!submissionData?.matters) {
      return generateSections(0, 0);
    }
    const publicMatterCount = submissionData.matters.filter(
      (m) => m.is_public,
    ).length;
    const confidentialMatterCount = submissionData.matters.filter(
      (m) => !m.is_public,
    ).length;

    return generateSections(publicMatterCount, confidentialMatterCount);
  }, [submissionData]);

  // Initialize form data when submission data is loaded
  useEffect(() => {
    if (submissionData) {
      const newInitialFormData = submissionToFormData(submissionData);
      setFormData(newInitialFormData);
      initialFormDataRef.current = newInitialFormData;

      const ensureRequiredTablesExist = async () => {
        if (!submissionId || !firmId || !supabase) return;

        try {
          await supabase.from("directory_submission_overview").upsert(
            {
              firm_id: firmId,
              directory_submission_id: parseInt(submissionId),
              firm_name: submissionData.firm_name,
            },
            {
              onConflict: "directory_submission_id",
            },
          );
        } catch (error) {
          console.error("Error ensuring required tables exist:", error);
        }
      };

      void ensureRequiredTablesExist();
    }
  }, [submissionData, submissionId, firmId, supabase]);

  // Clear form data when submissionId is undefined
  useEffect(() => {
    if (submissionId === undefined) {
      setFormData({});
      initialFormDataRef.current = null;
    }
  }, [submissionId]);

  // Subscribe to realtime form updates
  useRealtimeFormUpdates({
    supabase: supabase as SupabaseClient,
    submissionId,
    isLoadedSuccessfully,
  });

  // Manage field locking for multi-user collaboration
  const {
    lockedFields,
    lockedQuestions,
    userProfiles,
    unlockRequest,
    handleFieldFocus,
    handleFieldBlur,
    sendFieldUnlockRequest,
    handleAcceptUnlock,
    handleRejectUnlock,
    currentlyEditingField,
    requestFieldUnlock,
    requestingUnlockFor,
    setRequestingUnlockFor,
  } = useFieldLocking({
    supabase: supabase as SupabaseClient,
    submissionId,
    isLoadedSuccessfully,
  });

  // Load comments using the extracted utility function
  const { data: submissionComments = {} } = useQuery({
    queryKey: queryKeys.directorySubmissionComments(submissionId as string),
    queryFn: async (): Promise<QuestionComments> => {
      if (!submissionId) {
        throw new Error("No submissionId");
      }
      try {
        // Import loadCommentsData dynamically to avoid circular dependencies
        const { loadCommentsData } = await import(
          "@/components/DirectorySubmissions/Form/utils/loadCommentsData"
        );
        return await loadCommentsData(submissionId, supabase as SupabaseClient);
      } catch (err) {
        console.error("Error loading comments:", err);
        return {};
      }
    },
    enabled: !!submissionId && !!supabase,
    refetchOnWindowFocus: false,
    staleTime: 0,
    initialData: {},
  });

  // Load revision history using the extracted utility function
  const { data: submissionRevisions = {} } = useQuery({
    queryKey: queryKeys.directorySubmissionRevisions(submissionId as string),
    queryFn: async (): Promise<RevisionHistoryItems> => {
      if (!submissionId) {
        throw new Error("No submissionId");
      }
      try {
        // Import loadRevisionsData dynamically to avoid circular dependencies
        // The new implementation uses matter IDs as keys for matter questions
        // This ensures revisions are tied to matter IDs, not positions in the form
        const { loadRevisionsData } = await import(
          "@/components/DirectorySubmissions/Form/utils/loadRevisionsData"
        );
        return await loadRevisionsData(
          submissionId,
          supabase as SupabaseClient,
        );
      } catch (err) {
        console.error("Error loading revisions:", err);
        return {};
      }
    },
    enabled: !!submissionId && !!supabase,
    refetchOnWindowFocus: false,
    staleTime: 0,
    initialData: {},
  });

  const registerEditorRef = useCallback(
    (id: string, ref: EditorValueHandle | null) => {
      if (ref) editorRefs.current.set(id, ref);
      else editorRefs.current.delete(id);
    },
    [],
  );

  const getEditorValue = useCallback((id: string) => {
    const h = editorRefs.current.get(id);
    if (!h) return undefined;
    return "getHtml" in h ? h.getHtml() : h.checked ?? false;
  }, []);

  const updateField = useCallback((fieldId: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  }, []);

  const updateTableCell = useCallback(
    (questionId: string, rowIndex: number, colIndex: number, value: any) => {
      setFormData((prevData) => {
        // Create a deep copy of the current table
        const currentTable = [...((prevData[questionId] as TableRow[]) || [])];

        // Create or pad rows if needed
        while (currentTable.length <= rowIndex) {
          currentTable.push({
            value: [],
            row_order: currentTable.length + 1,
          });
        }

        // Deep clone the row we're modifying
        const rowToUpdate = { ...currentTable[rowIndex] };

        // Create a deep copy of the value array
        const valueArray = [...rowToUpdate.value];

        // Pad the array if needed to accommodate the column index
        while (valueArray.length <= colIndex) {
          valueArray.push("");
        }

        // Set the value at the specific column index
        valueArray[colIndex] = value;

        // Create the updated row with the modified value array
        const updatedRow = {
          ...rowToUpdate,
          value: valueArray,
        };

        // Create the new table with the updated row
        const newTable = [
          ...currentTable.slice(0, rowIndex),
          updatedRow,
          ...currentTable.slice(rowIndex + 1),
        ];

        // Return updated form data
        return {
          ...prevData,
          [questionId]: newTable,
        };
      });
    },
    [],
  );

  const saveFieldToDb = useCallback(
    async (fieldId: string, updatedValue: QuestionValue) => {
      if (
        !submissionId ||
        !initialFormDataRef.current ||
        !supabase ||
        !fieldId
      ) {
        return;
      }

      // Use the updatedValue that was passed to this function
      let latestValue = updatedValue;

      // check if there is a live editor for this exact field
      const editorValue = getEditorValue(fieldId);
      if (editorValue !== undefined) latestValue = editorValue;

      // check table cells
      if (Array.isArray(updatedValue)) {
        const tableClone = structuredClone(updatedValue) as TableRow[];
        tableClone.forEach((row, r) => {
          row.value.forEach((cell, c) => {
            const cellId = `${fieldId}-r${r}c${c}`;
            const editorValue = getEditorValue(cellId);
            if (editorValue !== undefined) row.value[c] = editorValue;
          });
        });
        latestValue = tableClone;
      }

      const changedFields = [fieldId].filter(
        (id) => !isEqual(latestValue, initialFormDataRef.current![id]),
      );

      if (changedFields.length === 0) return;

      setIsSaving(true);
      try {
        const result = await saveData({
          supabase,
          firmId: firmId ?? "",
          userId: userId ?? "",
          submissionId: parseInt(submissionId),
          enqueueSnackbar,
          formData: {
            ...formData,
            [fieldId]: latestValue,
          },
          initialFormData: initialFormDataRef.current,
          changedFields,
          skipQueryInvalidation: true,
        });

        // Update initialFormData AND formData with saved values
        if (result && typeof result === "object") {
          setFormData((prevFormData) => {
            const newFormData = { ...prevFormData };
            const newInitialData = initialFormDataRef.current
              ? { ...initialFormDataRef.current }
              : {};

            // Only process fields that were actually changed
            changedFields.forEach((key) => {
              const savedValue = result[key];
              if (savedValue === undefined) return;

              const clonedSavedValue = structuredClone(savedValue);

              // Update both formData and initialFormData with the exact same value
              // This ensures they stay in sync
              newFormData[key] = clonedSavedValue;
              if (initialFormDataRef.current) {
                newInitialData[key] = clonedSavedValue;
              }
            });

            // Update the ref *after* calculating new state
            initialFormDataRef.current = newInitialData;
            return newFormData;
          });
        }
      } catch (error) {
        console.error("Error saving fields:", error);
        enqueueSnackbar("Failed to save changes", {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "bottom" },
        });
        throw error;
      } finally {
        setIsSaving(false);
      }
    },
    [submissionId, formData, supabase, firmId, userId, enqueueSnackbar],
  );

  const hasUnsavedChanges = initialFormDataRef.current
    ? Object.keys(formData).some(
        (key) => !isEqual(formData[key], initialFormDataRef.current![key]),
      )
    : false;

  // Block navigation if there are unsaved changes and save them
  const blocker = useBlocker(
    ({
      currentLocation: { pathname: currentPath },
      nextLocation: { pathname: nextPath },
    }) => {
      return hasUnsavedChanges && currentPath !== nextPath;
    },
  );

  // Handle the blocked navigation
  useEffect(() => {
    if (blocker.state === "blocked") {
      setShowSavingModal(true);

      // Save changes then proceed with navigation
      const saveAndProceed = async () => {
        try {
          if (submissionId && initialFormDataRef.current) {
            const changedFields = Object.keys(formData).filter(
              (key) =>
                !isEqual(formData[key], initialFormDataRef.current![key]),
            );

            if (changedFields.length > 0) {
              // Import saveData directly to avoid circular dependencies
              const { saveData } = await import(
                "@/components/DirectorySubmissions/Form/utils/saveData"
              );

              await saveData({
                supabase,
                firmId,
                userId,
                submissionId: parseInt(submissionId),
                enqueueSnackbar,
                formData,
                initialFormData: initialFormDataRef.current,
                changedFields,
              });

              // Update initialFormData to reflect saved state
              initialFormDataRef.current = structuredClone(formData);
            }
          }
        } catch (error) {
          console.error("Error saving before navigation:", error);
          enqueueSnackbar("Failed to save changes", { variant: "error" });
        } finally {
          setShowSavingModal(false);
          // Proceed with the blocked navigation
          blocker.proceed();
        }
      };

      void saveAndProceed();
    }
  }, [
    blocker,
    formData,
    submissionId,
    supabase,
    firmId,
    userId,
    enqueueSnackbar,
  ]);

  // Show warning before unload if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  // Timeout for saving operations
  useEffect(() => {
    if (isSaving) {
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current);
      }

      savingTimeoutRef.current = setTimeout(() => {
        if (isSaving) {
          enqueueSnackbar(
            "Saving is taking longer than expected. Please try again.",
            { variant: "error" },
          );
          setIsSaving(false);
        }
      }, 10000);
    } else {
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current);
        savingTimeoutRef.current = null;
      }
    }

    return () => {
      if (savingTimeoutRef.current) {
        clearTimeout(savingTimeoutRef.current);
      }
    };
  }, [isSaving, enqueueSnackbar]);

  const { pendingSaves, saveField } = useFieldSaving({
    supabase,
    firmId,
    userId,
    submissionId,
    formData,
    initialFormData: initialFormDataRef.current,
    enqueueSnackbar,
    externalSaveFunction: saveFieldToDb,
  });

  const {
    sidePanelOpen,
    activeCommentQuestion,
    openSidePanel,
    closeSidePanel,
    setActiveCommentQuestion,
  } = useSidePanelState();

  const {
    approvedQuestions,
    pendingReviewQuestions,
    isQuestionApproved: baseIsQuestionApproved,
    isQuestionPendingReview: baseIsQuestionPendingReview,
  } = useApprovalStatus({
    submissionComments,
  });

  // Wrap the approval status functions to include formData
  const isQuestionApproved = useCallback(
    (questionId: string): boolean => {
      return baseIsQuestionApproved(questionId, formData);
    },
    [baseIsQuestionApproved, formData],
  );

  const isQuestionPendingReview = useCallback(
    (questionId: string): boolean => {
      return baseIsQuestionPendingReview(questionId, formData);
    },
    [baseIsQuestionPendingReview, formData],
  );

  // Function to update approval state after reordering
  const updateApprovalStateAfterReorder = useCallback(() => {
    // After reordering, the positions have changed but the matter IDs have not
    // Instead of trying to manually update the approval state, we should
    // just invalidate the comments query to refresh the data
    if (submissionId) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.directorySubmissionComments(
          submissionId.toString(),
        ),
      });
    }
  }, [submissionId, queryClient]);

  // Update lock state in the database
  const updateLockStateMutation = useMutation({
    mutationFn: async (isFormLocked: boolean) => {
      const { error } = await (supabase as SupabaseClient)
        .from("directory_submission")
        .update({ is_locked: isFormLocked })
        .eq("id", submissionId);
      if (error) throw new Error(error.message);
    },
    onSuccess: (_, isFormLocked) => {
      queryClient.setQueryData(
        queryKeys.directorySubmission(submissionId ?? ""),
        (oldData: any) => ({
          ...oldData,
          is_locked: isFormLocked,
        }),
      );
      setIsFormLocked(isFormLocked);
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update lock state: ${error.message}`, {
        variant: "error",
      });
    },
  });

  // Setup subscriptions
  useEffect(() => {
    if (!submissionId || !supabase || !queryClient) return;

    // Declare handleVisibilityChange first to avoid scoping issues
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        subscriptionManager.checkAndReconnectSubscriptions();
      }
    };

    const subscriptionManager = setupSubmissionSubscriptions(
      submissionId,
      supabase,
      queryClient,
      enqueueSnackbar,
      {
        onMatterAdded: (matter) => {
          // Guard: skip if we already have this matter id to prevent duplicates
          const alreadyExists = Object.values(formData).some(
            (v: any) => v?.id?.toString?.() === matter?.id?.toString(),
          );
          if (alreadyExists) {
            return;
          }

          // Create a key for the new matter
          const prefix = matter?.is_public ? "PM" : "CM";
          // Find the highest existing index for this type of matter
          const existingKeys = Object.keys(formData)
            .filter((key) => key.startsWith(prefix))
            .map((key) => parseInt(key.slice(2)))
            .filter((num) => !isNaN(num));

          const nextIndex =
            existingKeys.length > 0 ? Math.max(...existingKeys) + 1 : 1;

          const newKey = `${prefix}${nextIndex}`;

          // Create a default matter structure with empty fields
          const newMatterValue = {
            0: matter?.client_name || "",
            1: matter?.matter_description || "",
            2: matter?.value || "",
            3: matter?.cross_border || "",
            4: matter?.lead_partner_name || "",
            5: matter?.other_team_members || "",
            6: matter?.other_firms || "",
            7: matter?.date_of_completion || "",
            8: matter?.other_info || "",
            id: matter?.id?.toString() || "",
            included_order: matter?.included_order || nextIndex,
          };

          // Update the form data
          setFormData((prev) => ({
            ...prev,
            [newKey]: newMatterValue,
          }));

          // Also update the initialFormData to prevent "unsaved changes" warning
          if (initialFormDataRef.current) {
            initialFormDataRef.current[newKey] =
              structuredClone(newMatterValue);
          }

          // Clear saving state since the matter was successfully added
          setIsSaving(false);

          // Recalculate valid/empty field counts
          const totalFields = Object.keys(newMatterValue).length;
          const filledFields = Object.values(newMatterValue).filter(
            (value) => value !== null && value !== "",
          ).length;
          const emptyFields = totalFields - filledFields;

          // Trigger any necessary UI updates for field count
          if (submissionData && queryClient) {
            // Update the invalid_field_count in the submission data
            queryClient.setQueryData(
              queryKeys.directorySubmission(submissionId ?? ""),
              (oldData: any) => {
                if (!oldData) return oldData;

                // Update invalid_field_count in the submission data
                const newInvalidFieldCount =
                  (Number(oldData.invalid_field_count) || 0) +
                  Number(emptyFields);

                // Also directly update the database with new count
                if (supabase && submissionId) {
                  void (async () => {
                    try {
                      await supabase
                        .from("directory_submission")
                        .update({ invalid_field_count: newInvalidFieldCount })
                        .eq("id", submissionId);
                    } catch (err) {
                      console.error(
                        "Failed to update invalid_field_count:",
                        err,
                      );
                    }
                  })();
                }

                return {
                  ...oldData,
                  invalid_field_count: newInvalidFieldCount,
                };
              },
            );

            // Instead of trying to update initialFormData directly,
            // trigger a refetch to get the latest data
            void queryClient.invalidateQueries({
              queryKey: queryKeys.directorySubmission(submissionId ?? ""),
            });
          }
        },
      },
    );

    void subscriptionManager.setupAllSubscriptions();

    // Add debugging utilities to window for manual testing
    if (typeof window !== "undefined") {
      (window as any).checkSubscriptions =
        subscriptionManager.checkAndReconnectSubscriptions;

      // Subscription setup complete

      // Cleanup monitoring on unmount
      return () => {
        subscriptionManager.cleanup();
        delete (window as any).checkSubscriptions;
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      };
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      subscriptionManager.cleanup();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [
    submissionId,
    supabase,
    queryClient,
    enqueueSnackbar,
    setIsSaving,
    submissionData,
  ]);

  const { isFormLocked, setIsFormLocked, showLockWarning } = useFormLocking({
    initialLockState: submissionData?.is_locked ?? false,
    isSaving,
    enqueueSnackbar,
  });

  const value = useMemo(
    () => ({
      isSaving,
      setIsSaving,
      isFormLocked,
      submissionData: submissionData ?? null,
      formData,
      setFormData,
      initialFormData: initialFormDataRef.current,
      error,
      showLockWarning,
      updateLockState: updateLockStateMutation.mutate,
      sidePanelOpen,
      openSidePanel,
      closeSidePanel,
      submissionComments,
      submissionRevisions,
      activeCommentQuestion,
      setActiveCommentQuestion,
      saveField,
      pendingSaves,
      approvedQuestions,
      isQuestionApproved,
      pendingReviewQuestions,
      isQuestionPendingReview,
      updateField,
      updateTableCell,
      hasUnsavedChanges,
      updateApprovalStateAfterReorder,
      registerEditorRef,
      requestFieldUnlock,
      lockField: handleFieldFocus,
      unlockField: handleFieldBlur,
      lockedFields,
      lockedQuestions,
      userProfiles,
      sections,
      currentlyEditingField,
    }),
    [
      isSaving,
      setIsSaving,
      isFormLocked,
      submissionData,
      formData,
      setFormData,
      error,
      showLockWarning,
      updateLockStateMutation,
      sidePanelOpen,
      openSidePanel,
      closeSidePanel,
      submissionComments,
      submissionRevisions,
      activeCommentQuestion,
      setActiveCommentQuestion,
      saveField,
      pendingSaves,
      approvedQuestions,
      isQuestionApproved,
      pendingReviewQuestions,
      isQuestionPendingReview,
      updateField,
      updateTableCell,
      hasUnsavedChanges,
      updateApprovalStateAfterReorder,
      registerEditorRef,
      handleFieldBlur,
      handleFieldFocus,
      requestFieldUnlock,
      lockedFields,
      lockedQuestions,
      sections,
      currentlyEditingField,
      userProfiles,
    ],
  );

  const providerContent =
    submissionId && (isLoading || !submissionData) ? (
      <Spinner />
    ) : submissionId &&
      submissionData &&
      Object.keys(formData).some((key) => formData[key] !== undefined) ? (
      <>{children}</>
    ) : submissionId ? (
      <Spinner />
    ) : (
      children
    );

  return (
    <SubmissionsContext.Provider value={value}>
      {providerContent}
      {showSavingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="flex items-center space-x-3 rounded-lg bg-white p-6">
            <Spinner size="sm" />
            <span>Saving your changes...</span>
          </div>
        </div>
      )}
      {unlockRequest && (
        <UnlockRequestReceivedModal
          isOpen={!!unlockRequest}
          onReject={handleRejectUnlock}
          onAccept={handleAcceptUnlock}
          requestingUserDisplayName={unlockRequest.requestingUserDisplayName}
        />
      )}
      {requestingUnlockFor && (
        <RequestUnlockModal
          isOpen={!!requestingUnlockFor}
          onClose={() => {
            setRequestingUnlockFor(null);
          }}
          fieldId={requestingUnlockFor.fieldId}
          lockingUserId={requestingUnlockFor.lockingUserId}
          lockingUserDisplayName={requestingUnlockFor.lockingUserDisplayName}
          sendFieldUnlockRequest={sendFieldUnlockRequest}
        />
      )}
    </SubmissionsContext.Provider>
  );
};

export const useSubmissions = () => {
  const context = useContext(SubmissionsContext);
  if (!context) {
    throw new Error("useSubmissions must be used within a SubmissionsProvider");
  }
  return context;
};
